import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Divider,
  Avatar,
  Button,
  Tabs,
  Tab,
  Badge,
  Paper,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  CheckCircle,
  Error,
  Warning,
  Info,
  Schedule,
  TrendingUp,
  PostAdd,
  Person,
  Campaign,
  MoreVert,
  MarkAsUnread,
  Delete,
  Refresh,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';

const NotificationsDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const notificationTypes = [
    { label: 'All', value: 'all', icon: <NotificationsIcon /> },
    { label: 'Posts', value: 'posts', icon: <PostAdd /> },
    { label: 'Analytics', value: 'analytics', icon: <TrendingUp /> },
    { label: 'System', value: 'system', icon: <Info /> },
  ];

  useEffect(() => {
    if (open) {
      // Notifications are already loaded from context
      setLoading(false);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.id);

    const postId =
      notification.actionData?.postId ?? notification.actionData?.post_id;

    // Handle action
    switch (notification.action) {
      case 'view_post':
        if (postId) navigate(`/posts?post_id=${encodeURIComponent(postId)}`);
        break;
      case 'view_analytics':
        if (postId) {
          navigate(`/analytics?post_id=${encodeURIComponent(postId)}`);
        } else {
          navigate('/analytics');
        }
        break;
      case 'retry_post':
        // Implement retry logic
        console.log('Retrying post:', notification.actionData.postId);
        break;
      case 'view_schedule':
        navigate('/scheduling?tab=upcoming');
        break;
      case 'view_competitors':
        navigate('/competitors');
        break;
      case 'view_system':
        if (notification.actionData?.uptime) {
          navigate('/task-monitoring');
        }
        break;
      case 'view_report':
        navigate('/analytics?report=weekly');
        break;
      case 'create_content':
        navigate(
          '/ai-content?prompt=' +
            encodeURIComponent(notification.actionData?.suggestion || ''),
        );
        break;
      default:
        break;
    }
    onClose();
  };

  const handleMenuOpen = (event, notification) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = () => {
    if (selectedNotification) {
      markAsRead(selectedNotification.id);
    }
    handleMenuClose();
  };

  const handleDeleteNotification = () => {
    if (selectedNotification) {
      deleteNotification(selectedNotification.id);
    }
    handleMenuClose();
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'success':
        return <CheckCircle sx={{ color: '#238636' }} />;
      case 'error':
        return <Error sx={{ color: '#f85149' }} />;
      case 'warning':
        return <Warning sx={{ color: '#fb8500' }} />;
      case 'info':
      default:
        return <Info sx={{ color: '#58a6ff' }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'success':
        return '#238636';
      case 'error':
        return '#f85149';
      case 'warning':
        return '#fb8500';
      case 'info':
      default:
        return '#58a6ff';
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    const currentType = notificationTypes[activeTab]?.value;
    return currentType === 'all' || notification.type === currentType;
  });

  const unreadByType = {
    all: unreadCount,
    posts: notifications.filter((n) => !n.read && n.type === 'posts').length,
    analytics: notifications.filter((n) => !n.read && n.type === 'analytics')
      .length,
    system: notifications.filter((n) => !n.read && n.type === 'system').length,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 2,
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #30363d',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon sx={{ color: '#58a6ff' }} />
          </Badge>
          <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 600 }}>
            Notifications
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllAsRead}
              sx={{
                color: '#58a6ff',
                textTransform: 'none',
                fontSize: '0.75rem',
              }}
            >
              Mark all read
            </Button>
          )}
          <IconButton onClick={onClose} sx={{ color: '#8b949e' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #30363d',
            '& .MuiTab-root': {
              color: '#8b949e',
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
              '&.Mui-selected': {
                color: '#58a6ff',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#58a6ff',
            },
          }}
        >
          {notificationTypes.map((type, index) => (
            <Tab
              key={type.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {type.icon}
                  {type.label}
                  {unreadByType[type.value] > 0 && (
                    <Chip
                      label={unreadByType[type.value]}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.7rem',
                        bgcolor: '#f85149',
                        color: '#fff',
                        minWidth: 16,
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ minHeight: '400px' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <Typography sx={{ color: '#8b949e' }}>
                Loading notifications...
              </Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: '#21262d',
                border: '1px solid #30363d',
                m: 3,
              }}
            >
              <NotificationsIcon
                sx={{ fontSize: 48, color: '#8b949e', mb: 2 }}
              />
              <Typography variant="h6" sx={{ color: '#8b949e', mb: 1 }}>
                No notifications
              </Typography>
              <Typography variant="body2" sx={{ color: '#6e7681' }}>
                You're all caught up! New notifications will appear here.
              </Typography>
            </Paper>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      bgcolor: notification.read
                        ? 'transparent'
                        : 'rgba(88, 166, 255, 0.05)',
                      borderLeft: notification.read
                        ? 'none'
                        : `3px solid ${getPriorityColor(
                            notification.priority
                          )}`,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: notification.read
                          ? 'rgba(88, 166, 255, 0.05)'
                          : 'rgba(88, 166, 255, 0.1)',
                      },
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          bgcolor: `${getPriorityColor(
                            notification.priority
                          )}20`,
                          color: getPriorityColor(notification.priority),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getPriorityIcon(notification.priority)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.read ? 500 : 600,
                              color: '#f0f6fc',
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#6e7681' }}
                            >
                              {formatDistanceToNow(notification.timestamp, {
                                addSuffix: true,
                              })}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, notification)}
                              sx={{ color: '#8b949e' }}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#8b949e',
                            mt: 0.5,
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < filteredNotifications.length - 1 && (
                    <Divider sx={{ bgcolor: '#30363d' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: '#21262d',
            border: '1px solid #30363d',
          },
        }}
      >
        {selectedNotification && !selectedNotification.read && (
          <MenuItem onClick={handleMarkAsRead} sx={{ color: '#f0f6fc' }}>
            <MarkAsUnread sx={{ mr: 1, fontSize: 20 }} />
            Mark as read
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteNotification} sx={{ color: '#f85149' }}>
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default NotificationsDialog;
